<template lang="pug">
	.sonic-scribe--selection-tool(:style="selectionStyles", @mousedown="onMouseDown", @mousemove="onMouseMove")
		.selection(v-if="info.selectionMode === 'range'")
			.time.flex-row.align-center.center(v-if="end")
				span {{ $Data.secondsToDisplayValue(selectedRange[1] - selectedRange[0], { mFormat: 'mm:ss.SSS', preventRound: true }) }}
		.groups-holder(:class="{ 'hand-tool': isHandTool }")
			.group(v-for="group in info.groups", 
				:style="{ '--start': `${getGroupPosition(group)}px`, '--width': `${getGroupWidth(group)}px` }",
				:class="{ focusable: isSlipTool, slip: isSlipTool }", @mousedown="e => onGroupMouseDown(e, group)")
				.group-mover.right.flex-row.align-center.center(v-if="isBlade && group.duration", @mouseover="isGroupMover = true", @mouseout="isGroupMover = false", @mousedown="e => onGroupMoverMouseDown(e, group, 'end')")
					fas(icon="left-right")
		.cutter(v-if="isCutterVisible", ref="cutter", :style="{ '--start': `${cutterPosition}px`}")
			.icon
				fas(icon="fas fa-scissors")
</template>

<script>
	import _ from 'lodash';

	export default {
		translationCategory: 'sonicScribe.BottomPanel',
		name: 'sonic-scribe--selection-tool',
		components: {  },
		props: ['info'],
		data() {
			return {
				start: 1,
				end: 2,
				cutterPosition: 0,
				isGroupMover: false,
				isGroupMoverMouseDown: false,
				groupOffsets: {}
			};
		},
		computed: {
			selectionStyles() {
				return {
					'--start': `${Math.max((this.selectedRange[0] - this.selectedRange[1]) * this.info.pixelPerSecond - 100, this.info.pixelPerSecond * (this.selectedRange[0] - this.info.position))}px`,
					'--width': `${Math.max(0, this.info.pixelPerSecond * (this.selectedRange[1] - this.selectedRange[0]))}px`,
				}
			},
			isSlipTool() {
				return ['slip'].includes(this.info.selectionMode);
			},
			isBlade() {
				return ['blade'].includes(this.info.selectionMode);
			},
			isCutterVisible() {
				return ['blade'].includes(this.info.selectionMode) && !this.isGroupMover && !this.isGroupMoverMouseDown;
			},
			isHandTool() {
				return ['hand'].includes(this.info.selectionMode);
			},
			selectedRange() {
				let start = Math.min(this.start, this.end);
				let end = Math.max(this.start, this.end);
				return [start, end];
			}
		},
		watch: {
			'info.selectionMode'() {
				this.start = null;
				this.end = null;
			}
		},
		mounted() {			
			if (__DEBUG__)
				window.selectionTool = this;

			document.body.addEventListener('mouseup', this.onBodyMouseUp);
			document.body.addEventListener('mousemove', this.onBodyMouseMove);
			this.$eventsBus.$on('sonic-scribe-select-word', this.onSonicScribeSelectWord);
		},
		destroyed() {
			document.body.removeEventListener('mouseup', this.onBodyMouseUp);
			document.body.removeEventListener('mousemove', this.onBodyMouseMove);
			this.$eventsBus.$off('sonic-scribe-select-word', this.onSonicScribeSelectWord);

			delete this.$isSettingSelection;
			delete this.$group;
			delete this.$groupMoverMode;
		},
		methods: {
			onSonicScribeSelectWord() {
				this.start = null;
				this.end = null;
			},
			getGroupPosition(group) {
				let groupOffsets = this.groupOffsets[group.id] || {};
				return this.info.getDisplayPosition(group.offset + (groupOffsets.offset || 0));
			},
			getGroupWidth(group) {
				let groupOffsets = this.groupOffsets[group.id] || {};
				return this.info.getDisplayPosition(group.offset + (group.duration || this.info.buffer.duration) + (groupOffsets.duration || 0)) - this.info.getDisplayPosition(group.offset);
			},
			onGroupMouseDown(e, group) {
				if (this.isSlipTool || this.isHandTool) {
					this.$isGroupMovingOffset = true;
					this.$group = group;
					this.groupOffsets = {};
				}
			},
			onGroupMoverMouseDown(e, group, mode) {
				this.isGroupMoverMouseDown = true;
				this.$group = group;
				this.$groupMoverMode = mode;
				this.groupOffsets = {};
			},
			onBodyMouseUp(e) {
				if (this.$isSettingSelection) {
					this.$eventsBus.$emit('sonic-scribe-select-words-by-range', this.selectedRange[0], this.selectedRange[1]);
				}
				this.$isSettingSelection = false;
				if (this.isGroupMoverMouseDown) {
					this.$emit('build');
				}
				this.isGroupMoverMouseDown = false;
				this.$isGroupMovingOffset = false;

				if (Object.keys(this.groupOffsets).length) {
					this.$emit('groups-changed', _.cloneDeep(this.groupOffsets));

					this.$eventsBus.$emit('sonic-scribe-add-to-history', {
						text: this.$tCaption('Group offsets changed'),
						type: 'group',
						groupId: this.$group?.id,
						groupOffsets: this.groupOffsets,
						confirmed: true
					});

					Object.keys(this.groupOffsets).map(groupId => {
						this.$eventsBus.$emit('sonic-scribe-group-offset-changed', groupId, this.groupOffsets[groupId]);
					});
					
					this.groupOffsets = {};
				}
			},
			addGroupOffset(group, prop, value, replaceValue) {
				if (!this.groupOffsets[group.id]) {
					this.$set(this.groupOffsets, group.id, {});
				}

				this.$set(this.groupOffsets[group.id], prop, replaceValue ? value : (this.groupOffsets[group.id][prop] || 0) + value);

				this.$eventsBus.$emit('sonic-scribe-group-offset-changing', group.id, this.groupOffsets[group.id]);
				this.$emit('groups-changing', this.groupOffsets);
			},
			onBodyMouseMove(e) {
				if (this.$isSettingSelection) {
					this.end = (e.offsetX / this.info.pixelPerSecond) + this.info.position;
				}

				if (this.isGroupMoverMouseDown && this.$group) {
					let newTimePos = (e.offsetX / this.info.pixelPerSecond) + this.info.position;

					if ((newTimePos - this.$group.offset) < 0.1) return;

					switch(this.$groupMoverMode) {
						case 'end': {
							let delta = newTimePos - (this.$group.offset + this.$group.duration);							
							
							this.addGroupOffset(this.$group, 'duration', delta, true);
							this.info.groups.filter(f => f.offset > this.$group.offset && f.id !== this.$group.id).map(f => {
								this.addGroupOffset(f, 'offset', delta, true);
							});
							break;
						}
					}
				}

				if (this.$isGroupMovingOffset) {
					let timeDelta = (e.movementX / this.info.pixelPerSecond);

					if (this.isSlipTool && this.$group) {
						this.addGroupOffset(this.$group, 'startOffset', timeDelta * -1);
					}

					if (this.isHandTool) {
						this.info.groups.map(f => this.addGroupOffset(f, 'startOffset', timeDelta * -1));
					}
				}
			},
			onMouseDown(e) {
				if (['range'].includes(this.info.selectionMode)) {
					this.$isSettingSelection = true;
					this.start = (e.offsetX / this.info.pixelPerSecond) + this.info.position;
					this.end = this.start;
					this.$eventsBus.$emit('sonic-scribe-clear-selection');
				}

				if (this.isCutterVisible) {
					this.$emit('cut', this.info.position + this.cutterPosition / this.info.pixelPerSecond);
				}
			},
			onMouseMove(e) {
				if (this.isCutterVisible && e.target === this.$el) {
					this.cutterPosition = e.offsetX;
				}
			}
		}
	}
</script>
<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.sonic-scribe--selection-tool {
		position: absolute;
		left: 0px;
		top: var(--canvasTop);
		width: 100%;
		bottom: 20px;

		.selection {
			position: absolute;
			background: #ffffff99;
			border: 0.5px solid #e7e7e7;
			border-left: 0.5px dashed $base-accent-transparent;
			border-right: 0.5px dashed $base-accent-transparent;
			top: 0px;
			bottom: -20px;
			left: var(--start);
			width: var(--width);
			pointer-events: none;
			background: repeating-linear-gradient(
				-45deg,
				#ffffff99,
				#ffffff99 30px,
				#ffffff77 30px,
				#ffffff77 60px,
			);

			> .time {
				position: absolute;
				right: 2px;
				top: 2px;
				//transform: translateY(-50%);
				font-size: 10px;
				color: #999;
			}
		}

		.groups-holder {
			position: absolute;
			top: 0px;
			bottom: -20px;
			left: 0px;
			width: 100%;
			overflow: hidden;
			pointer-events: none;
			
			&.hand-tool {
				pointer-events: all;
				cursor: w-resize;

				.group {
					pointer-events: all;					
					background: #ffffff55;
					border-color: $base-accent;
				}
			}

			.group {
				position: absolute;
				top: 0px;
				bottom: 0px;
				width: var(--width);
				left: var(--start);
				background: transparent;
				border-left: 2px solid #00000022;
				border-right: 2px solid #00000022;
				border-radius: 8px;
				pointer-events: none;

				&.focusable, .selected {
					pointer-events: all;

					&:hover {
						background: #ffffff55;
						border-color: $base-accent;
					}

					&.slip {
						cursor: w-resize;
					}
				}

				.group-mover {
					position: absolute;
					top: 50%;
					transform: translateY(-50%);
					width: 30px;
					height: 30px;
					border-radius: 50%;
					pointer-events: all;
					cursor: w-resize;
					color: #33333366;

					&.left {
						left: 0px;
					}

					&.right {
						right: 0px;
					}

					&:hover {
						background: #ffffff88;
						color: #333;
					}
				}
			}
		}

		.cutter {
			position: absolute;
			top: 0px;
			bottom: -20px;
			background-image: linear-gradient(#FF000088 33%, rgba(255,255,255,0) 0%);
			background-position: right;
			background-size: 1.5px 7px;
			background-repeat: repeat-y;
			width: 2px;
			left: calc(var(--start) + 1px);

			> .icon {
				position: absolute;
				bottom: 0px;
				left: 1px;
				transform: translateX(-50%) rotate(-90deg);
				color: #FF000088;
			}
		}
	}
</style>
